import React, { useEffect, useState } from "react"
import { Modal, Spinner } from "react-bootstrap"
import ReactQuill from "react-quill"
import "react-quill/dist/quill.snow.css"
import Select from "react-select"
import { format } from "date-fns"
import {
  PRODUCT_CODE_ARCHITECTURAL_PLANS,
  PRODUCT_CODE_STORMWATER_PLANS,
  PRODUCT_CODE_PLAN_REQUIREMENTS
} from "../../../../../utils/constants"

const SendAccessEmailModal = ({
  show, onHide, jobService, sentJobServiceAccessEmail
}) => {
  const {
    attributes: {
      contactsWithEmail, ccContactOptions, accountType, cost, activeFlag, bookingDates
    }
  } = jobService
  const mainPersons = contactsWithEmail.filter(jobContact => jobContact.contactType === "Main Person")
  const invoicePersons = contactsWithEmail.filter(jobContact => jobContact.contactType === "Invoice")
  const defaultOption = invoicePersons.length ? invoicePersons[0] : mainPersons[0]
  const toContactOptionsList = contactsWithEmail.map(jobContact => ({ value: jobContact.id, label: `${jobContact.name} (${jobContact.contactType})`, name: jobContact.name }))
  const [currentContact, setCurrentContact] = useState({})
  const [contactsToBeCc, setContactsToBeCc] = useState([])
  const [displayedCcContacts, setDisplayedCcContacts] = useState([])

  const emailTemplate = `
    <p>Hi {{Name}},</p>
    <p>We have booked in a Surveyor for the following date/s</p>
    {{BookingDates}}
    {{CheckboxMessages}}
    <p>If you can please confirm access by clicking one of the options below and then completing the form.</p>
    ${accountType === "50% - 50%" && Number(cost) !== 0 && activeFlag.data === null ? "<p>The booking is pending a 50% deposit prior to attending site, a separate email with the invoice details will be sent from the C&A Accounts Team.</p><p><br></p>" : ""}
  `
  const [message, setMessage] = useState(emailTemplate)

  const [additionalCc, setAdditionalCc] = useState("")
  const [loading, setLoading] = useState(false)

  // Checkbox options state
  const [selectedOptions, setSelectedOptions] = useState({
    architectural: false,
    architecturalDwg: false,
    stormwater: false,
    planRequirements: false
  })

  // Product code detection
  const jobServiceCode = jobService.attributes?.code
  const showArchitectural = PRODUCT_CODE_ARCHITECTURAL_PLANS.includes(jobServiceCode)
  const showStormwater = PRODUCT_CODE_STORMWATER_PLANS.includes(jobServiceCode)
  const showPlanRequirements = PRODUCT_CODE_PLAN_REQUIREMENTS.includes(jobServiceCode)

  // Checkbox message templates
  const getCheckboxMessages = () => {
    const messages = []
    if (selectedOptions.architectural) {
      messages.push("<p><strong>Kindly send through the Architectural plans in PDF format</strong></p>")
    }
    if (selectedOptions.architecturalDwg) {
      messages.push("<p><strong>Kindly send through the Architectural plans in PDF + DWG (AutoCad) format</strong></p>")
    }
    if (selectedOptions.stormwater) {
      messages.push("<p><strong>Kindly send through the Stormwater plans in PDF format</strong></p>")
    }
    if (selectedOptions.planRequirements) {
      // For PLAN-REQ, try to get parent product name, fallback to placeholder
      const parentProductName = jobService.attributes?.bookedInForJobService?.productName || "Plan of Redefinition"
      messages.push(`<p><strong>This site visit is for the Boundary Pegging which is part of the ${parentProductName}</strong></p>`)
    }
    return messages
  }

  const handleContacts = (e, contact) => {
    const tmpArray = [...contactsToBeCc]
    if (e.target.checked) {
      tmpArray.push(parseInt(contact.value, 10))
    } else {
      _.pull(tmpArray, parseInt(contact.value, 10))
    }

    setContactsToBeCc(tmpArray)
  }

  useEffect(() => {
    const newDisplayedCcContacts = ccContactOptions.map(jobContact => ({ value: jobContact.value, label: jobContact.label }))
    if (newDisplayedCcContacts.some(jobContact => jobContact.value === currentContact.value)) {
      const updatedCcList = newDisplayedCcContacts.filter(jobContact => jobContact.value !== currentContact.value)
      setDisplayedCcContacts(updatedCcList)
    }
  }, [currentContact])

  const onSubmit = () => {
    const normalizedSelectedOptions = {
      architectural: selectedOptions.architectural,
      architectural_dwg: selectedOptions.architecturalDwg,
      stormwater: selectedOptions.stormwater,
      plan_requirements: selectedOptions.planRequirements
    }
    const emailOptions = {
      to: currentContact.value,
      cc: contactsToBeCc,
      additional_cc: additionalCc,
      message,
      selected_options: normalizedSelectedOptions
    }
    setLoading(true)
    sentJobServiceAccessEmail(emailOptions)
    setAdditionalCc("")
    onHide()
  }

  useEffect(() => {
    if (show) {
      setLoading(false)
      setDisplayedCcContacts(ccContactOptions.length ? ccContactOptions.map(jobContact => ({ value: jobContact.value, label: jobContact.label })) : [])
      setCurrentContact(defaultOption ? { value: defaultOption.id, label: `${defaultOption.name} (${defaultOption.contactType})`, name: defaultOption.name } : toContactOptionsList[0])
      // Reset checkbox options when modal opens
      setSelectedOptions({
        architectural: false,
        architecturalDwg: false,
        stormwater: false,
        planRequirements: false
      })
    }
  }, [show])


  useEffect(() => {
    let newEmailTemplate = emailTemplate
    if (currentContact && currentContact.value) {
      newEmailTemplate = emailTemplate.replace("{{Name}}", currentContact.name)
      if (additionalCc || contactsToBeCc.length > 0) {
        newEmailTemplate = emailTemplate.replace("{{Name}}", "All")
      }
    }
    if (bookingDates && bookingDates.length > 0) {
      const bookingDatesStr = bookingDates.map(date => `<p>- ${format(new Date(date), "EEEE, dd MMM yyyy")}</p>`).join("")
      newEmailTemplate = newEmailTemplate.replace("{{BookingDates}}", bookingDatesStr)
    }

    // Insert checkbox messages after booking dates
    const checkboxMessages = getCheckboxMessages().join("")
    newEmailTemplate = newEmailTemplate.replace("{{CheckboxMessages}}", checkboxMessages)

    setMessage(newEmailTemplate)
  }, [currentContact, contactsToBeCc, additionalCc, bookingDates, selectedOptions])

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header
        className="pb-3"
        closeButton>
        <Modal.Title>
          <h3 className="modal-title">Choose Who to Send To</h3>
        </Modal.Title>
      </Modal.Header>
      <div className="modal-body px-md-5">
        <div className="form-group row">
          <label className="col-2 col-form-label">To</label>
          <div className="col-10">
            <Select
              className="assign-dropdown w-75"
              name="client"
              isSearchable
              placeholder="Select Client"
              options={toContactOptionsList.length ? toContactOptionsList : []}
              value={currentContact}
              onChange={e => {
                setCurrentContact(e);
              }} />
          </div>
        </div>

        <div className="form-group row">
          <label className="col-2 col-form-label">CC</label>
          <form className="col-10">
            <div className="mb-4">
              {!!displayedCcContacts?.length && displayedCcContacts.map(contact => (
                <div className="custom-control custom-checkbox mb-2" key={contact.value}>
                  <input
                    className="custom-control-input"
                    id={contact.value}
                    type="checkbox"
                    onChange={e => handleContacts(e, contact)} />
                  <label className="custom-control-label" htmlFor={contact.value}>{contact.label}</label>
                </div>
              ))}
            </div>
          </form>
        </div>

        <div className="form-group row">
          <div className="col-2"></div>
          <div className="col-10">
            <label htmlFor="email">Additional Non-Contacts Cc</label>
            <input
              name="additional_cc"
              className="form-control"
              type="email"
              id="additional_cc"
              placeholder="Email"
              onChange={e => setAdditionalCc(e.target.value)} />
          </div>
        </div>

        {/* Checkbox Options Section */}
        {(showArchitectural || showStormwater || showPlanRequirements) && (
          <div className="form-group row">
            <div className="col-10">
              {showArchitectural && (
                <>
                  <div className="custom-control custom-checkbox mb-2">
                    <input
                      className="custom-control-input"
                      id="architectural-pdf"
                      type="checkbox"
                      checked={selectedOptions.architectural}
                      onChange={e => setSelectedOptions(prev => ({ ...prev, architectural: e.target.checked }))} />
                    <label className="custom-control-label" htmlFor="architectural-pdf">
                      Architectural
                    </label>
                    <div className="text-muted small mt-1">
                      Kindly send through the Architectural plans in PDF format
                    </div>
                  </div>
                  <div className="custom-control custom-checkbox mb-2">
                    <input
                      className="custom-control-input"
                      id="architectural-dwg"
                      type="checkbox"
                      checked={selectedOptions.architecturalDwg}
                      onChange={e => setSelectedOptions(prev => ({ ...prev, architecturalDwg: e.target.checked }))} />
                    <label className="custom-control-label" htmlFor="architectural-dwg">
                      Architectural +DWG
                    </label>
                    <div className="text-muted small mt-1">
                      Kindly send through the Architectural plans in PDF + DWG (AutoCad) format
                    </div>
                  </div>
                </>
              )}
              {showStormwater && (
                <div className="custom-control custom-checkbox mb-2">
                  <input
                    className="custom-control-input"
                    id="stormwater"
                    type="checkbox"
                    checked={selectedOptions.stormwater}
                    onChange={e => setSelectedOptions(prev => ({ ...prev, stormwater: e.target.checked }))} />
                  <label className="custom-control-label" htmlFor="stormwater">
                    Stormwater
                  </label>
                  <div className="text-muted small mt-1">
                    Kindly send through the Stormwater plans in PDF format
                  </div>
                </div>
              )}
              {showPlanRequirements && (
                <div className="custom-control custom-checkbox mb-2">
                  <input
                    className="custom-control-input"
                    id="plan-requirements"
                    type="checkbox"
                    checked={selectedOptions.planRequirements}
                    onChange={e => setSelectedOptions(prev => ({ ...prev, planRequirements: e.target.checked }))} />
                  <label className="custom-control-label" htmlFor="plan-requirements">
                    Plan Requirements
                  </label>
                  <div className="text-muted small mt-1">
                    This site visit is for the Boundary Pegging which is part of the
                    {" "}
                    {`{Parent Product Full Name}`}
                    {" "}
                    i.e. &ldquo;
                    {jobService.attributes?.bookedInForJobService?.productName || "Plan of Redefinition"}
                    &rdquo;
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="form-group">
          <ReactQuill
            value={message}
            onChange={value => setMessage(value)} />
        </div>

        <div className="modal-footer d-flex align-items-center justify-content-between px-0 py-4">
          <button
            className="btn btn-blue btn-lg"
            onClick={onHide}
            type="button">
            Back
          </button>
          <button
            className="btn btn-red btn-lg"
            onClick={onSubmit}
            disabled={!toContactOptionsList?.length || loading}
            type="submit">
            Send to Client
            {loading && <Spinner className="ml-1" animation="border" role="status" size="sm" />}
          </button>
        </div>
      </div>
    </Modal>
  )
}
export default SendAccessEmailModal
